import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { SIDEBAR_CHECKOUT_CONTENT } from './constants';
import styles from './sidebarPowerCheckout.scss';

const formatPrice = (price) => {
    if (!price) {
        return price;
    }
    return price.replace(/\.00$/, '');
};

const PricingPlaceholder = () => (
    <div className={styles.pricingSummary}>
        <div className={styles.pricingRow}>
            <div>
                <div className={styles.pricingPlaceholderPrice} />
                <div className={styles.pricingPlaceholderMonthly} />
            </div>
            <div className={styles.pricingPlaceholderRenewal} />
        </div>
    </div>
);

const PricingSummary = ({
    isPastDueAnnual,
    annualPastDueDetails,
    isMonthlyUser,
    isPastDueMonthly,
    monthlyHook,
    freeUserHook,
    freeRenewalDate,
}) => {
    // Scenario 4: Past-due annual — use the existing subscription's pricing
    if (isPastDueAnnual) {
        if (!annualPastDueDetails?.displayAmount) {
            return <PricingPlaceholder />;
        }

        const renewalDate = annualPastDueDetails.renewalDate
            ? moment(annualPastDueDetails.renewalDate).format('MMM D, YYYY')
            : '';

        const monthlyAmount = annualPastDueDetails.amount
            ? `$${(annualPastDueDetails.amount / 12).toFixed(0)}`
            : null;

        return (
            <div className={styles.pricingSummary}>
                <div className={styles.pricingRow}>
                    <div>
                        <div className={styles.pricingAmount}>
                            {formatPrice(annualPastDueDetails.displayAmount)}
                        </div>
                        {monthlyAmount && (
                            <div className={styles.pricingMonthly}>
                                {monthlyAmount}{SIDEBAR_CHECKOUT_CONTENT.pricingMonthly}
                            </div>
                        )}
                    </div>
                    {renewalDate && (
                        <div className={styles.pricingRenewal}>
                            {SIDEBAR_CHECKOUT_CONTENT.renewalDateLabel}
                            <span className={styles.pricingRenewalDate}>{renewalDate}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Scenarios 2 & 3: Monthly user pricing
    if (isMonthlyUser || isPastDueMonthly) {
        const plan = monthlyHook.upgradeData?.annualPlan;
        const renewalDate = monthlyHook.upgradeData?.renewalDate
            ? moment(monthlyHook.upgradeData.renewalDate).format('MMM D, YYYY')
            : '';

        if (monthlyHook.loading || !plan) {
            return <PricingPlaceholder />;
        }

        return (
            <div className={styles.pricingSummary}>
                <div className={styles.pricingRow}>
                    <div>
                        <div className={styles.pricingAmount}>
                            {formatPrice(plan.displayAmountPayPerRecurringInterval || plan.displayAnnualAmount)}
                        </div>
                        {plan.displayMonthlyAmount && (
                            <div className={styles.pricingMonthly}>
                                {formatPrice(plan.displayMonthlyAmount)}{SIDEBAR_CHECKOUT_CONTENT.pricingMonthly}
                            </div>
                        )}
                    </div>
                    {renewalDate && (
                        <div className={styles.pricingRenewal}>
                            {SIDEBAR_CHECKOUT_CONTENT.renewalDateLabel}
                            <span className={styles.pricingRenewalDate}>{renewalDate}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Scenario 1: Free user pricing
    const yearlyPlan = freeUserHook.getYearlyPlan();
    if (freeUserHook.isLoadingPricing || !yearlyPlan) {
        return <PricingPlaceholder />;
    }

    return (
        <div className={styles.pricingSummary}>
            <div className={styles.pricingRow}>
                <div>
                    <div className={styles.pricingAmount}>
                        {formatPrice(yearlyPlan.displayAmountPayPerRecurringInterval)}
                    </div>
                    {yearlyPlan.displayMonthlyAmount && (
                        <div className={styles.pricingMonthly}>
                            {formatPrice(yearlyPlan.displayMonthlyAmount)}{SIDEBAR_CHECKOUT_CONTENT.pricingMonthly}
                        </div>
                    )}
                </div>
                <div className={styles.pricingRenewal}>
                    {SIDEBAR_CHECKOUT_CONTENT.renewalDateLabel}
                    <span className={styles.pricingRenewalDate}>{freeRenewalDate}</span>
                </div>
            </div>
        </div>
    );
};

PricingSummary.propTypes = {
    isPastDueAnnual: PropTypes.bool.isRequired,
    annualPastDueDetails: PropTypes.shape({
        displayAmount: PropTypes.string,
        amount: PropTypes.number,
        renewalDate: PropTypes.string,
    }),
    isMonthlyUser: PropTypes.bool.isRequired,
    isPastDueMonthly: PropTypes.bool.isRequired,
    monthlyHook: PropTypes.shape({
        loading: PropTypes.bool,
        upgradeData: PropTypes.shape({
            annualPlan: PropTypes.shape({
                displayAmountPayPerRecurringInterval: PropTypes.string,
                displayAnnualAmount: PropTypes.string,
                displayMonthlyAmount: PropTypes.string,
            }),
            renewalDate: PropTypes.string,
        }),
    }).isRequired,
    freeUserHook: PropTypes.shape({
        isLoadingPricing: PropTypes.bool,
        getYearlyPlan: PropTypes.func,
    }).isRequired,
    freeRenewalDate: PropTypes.string.isRequired,
};

PricingSummary.defaultProps = {
    annualPastDueDetails: null,
};

export default PricingSummary;
